import { PUBLIC_EMAIL_DOMAIN_RE } from '@planuze/admin-contracts';
import { Button, InlineAlert, Input, Spinner, toast } from '@planuze/ui';
import { XIcon } from 'lucide-react';
import { useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import { usePublishersContent } from '../../content/sections/publishers.js';
import {
  usePublicEmailDomains,
  useUpdatePublicEmailDomains,
} from '../../hooks/use-public-email-domains.js';
import { configServiceErrorMessage } from '../../lib/config-service-error.js';

/* Painel de curadoria de domínios de email públicos (ADR-0569, super_admin). Lista LIVE do
   config-service; add/remove faz PUT da lista inteira (replace, RMW no admin-service). Domínios
   aqui são BARRADOS como domínio `verified` de org (footgun same-domain auto-join, ADR-0325/0551);
   remover um domínio (ex.: gmail.com) libera o cadastro de org com esse domínio. */
export function PublicEmailDomainsCard() {
  const copy = usePublishersContent();
  const { locale } = useAdminLocale();
  const { data, isLoading, isError } = usePublicEmailDomains();
  const update = useUpdatePublicEmailDomains();
  const [draft, setDraft] = useState('');

  const domains = data ?? [];
  const busy = update.isPending;

  const commit = (next: readonly string[]): void => {
    update.mutate(
      { domains: [...next] },
      {
        onError: (err) =>
          toast.error(configServiceErrorMessage(err, copy.publicEmailDomains.saveError, locale)),
      },
    );
  };

  const add = (): void => {
    const d = draft.trim().toLowerCase();
    if (d.length === 0) return;
    if (!PUBLIC_EMAIL_DOMAIN_RE.test(d)) {
      toast.error(copy.publicEmailDomains.invalidDomain);
      return;
    }
    if (domains.includes(d)) {
      toast.error(copy.publicEmailDomains.duplicateDomain);
      return;
    }
    commit([...domains, d]);
    setDraft('');
  };

  const remove = (domain: string): void => commit(domains.filter((d) => d !== domain));

  return (
    <section className="mb-4 flex flex-col gap-3 rounded-md border border-border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-base text-foreground">{copy.publicEmailDomains.title}</h2>
        <p className="text-muted-foreground text-sm">{copy.publicEmailDomains.description}</p>
      </div>

      {isError ? (
        <InlineAlert variant="destructive">{copy.publicEmailDomains.loadError}</InlineAlert>
      ) : isLoading ? (
        <div className="grid place-items-center p-4">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {domains.length === 0 ? (
            <span className="text-muted-foreground text-sm">{copy.publicEmailDomains.empty}</span>
          ) : (
            domains.map((domain) => (
              <span
                key={domain}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 font-mono text-foreground text-xs"
              >
                {domain}
                <button
                  type="button"
                  aria-label={copy.publicEmailDomains.removeAriaLabel(domain)}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                  disabled={busy}
                  onClick={() => remove(domain)}
                >
                  <XIcon className="size-3" />
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add();
        }}
      >
        <Input
          value={draft}
          placeholder={copy.publicEmailDomains.placeholder}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          className="max-w-xs"
        />
        <Button type="submit" variant="outline" size="sm" loading={busy} disabled={busy}>
          {copy.publicEmailDomains.add}
        </Button>
      </form>
    </section>
  );
}
